'use client'

import { createClient } from '@/core/supabase/client'

type FileItem = {
  url: string
  alt: string
}

export const getFilesUrls = async (paths: string[]): Promise<FileItem[]> => {
  const expiresIn = 3600 * 6 // 6 hours
  const client = createClient()

  if (!paths || !Array.isArray(paths) || paths.length === 0) {
    return []
  }

  // Filtrar apenas paths válidos (strings não vazias)
  // Normalizar paths: remover barras iniciais e garantir formato correto
  const validPaths = paths
    .filter((path): path is string => typeof path === 'string' && path.trim().length > 0)
    .map((path) => {
      // Remover barra inicial se existir
      const normalized = path.trim().replace(/^\/+/, '')
      // Se o path começar com 'app/', remover esse prefixo
      if (normalized.startsWith('app/')) {
        return normalized.replace(/^app\//, '')
      }
      return normalized
    })
    .filter((path) => path.length > 0)

  if (validPaths.length === 0) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[getFilesUrls] Nenhum path válido após normalização. Paths originais:', paths)
    }
    return []
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('[getFilesUrls] Paths normalizados:', validPaths)
  }

  // Verificar se há sessão ativa antes de tentar criar URLs assinadas
  try {
    const { data: sessionData, error: sessionError } = await client.auth.getSession()

    if (sessionError || !sessionData?.session) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[getFilesUrls] Sem sessão ativa, tentando URLs públicas diretamente')
      }
      // Se não houver sessão, tentar URLs públicas diretamente
      return getPublicUrls(validPaths, client)
    }

    // Tentar criar URLs assinadas
    const { data, error } = await client.storage.from('app').createSignedUrls(validPaths, expiresIn)

    if (error) {
      console.error('[getFilesUrls] Erro ao criar URLs assinadas:', error)
      // Tentar usar URLs públicas como fallback
      return getPublicUrls(validPaths, client)
    }

    const output: FileItem[] = []
    const failedPaths: string[] = []

    if (data) {
      for (let idx = 0; idx < data.length; idx++) {
        const item = data[idx]

        if (item && !item.error && item.signedUrl) {
          output.push({ url: item.signedUrl, alt: `item ${idx}` })
          if (process.env.NODE_ENV === 'development') {
            console.log(`[getFilesUrls] URL assinada criada para ${validPaths[idx]}`)
          }
        } else if (item?.error) {
          console.warn(
            `[getFilesUrls] Erro ao criar URL assinada para path ${validPaths[idx]}:`,
            item.error,
          )
          failedPaths.push(validPaths[idx])
        }
      }
    }

    // Se houver paths que falharam, tentar URLs públicas como fallback
    if (failedPaths.length > 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log(
          `[getFilesUrls] Tentando URLs públicas para ${failedPaths.length} paths que falharam`,
        )
      }
      const publicUrls = await getPublicUrls(failedPaths, client)
      output.push(...publicUrls)
    }

    return output
  } catch (error) {
    console.error('[getFilesUrls] Erro inesperado:', error)
    // Tentar usar URLs públicas como fallback
    return getPublicUrls(validPaths, client)
  }
}

// Função auxiliar para tentar obter URLs públicas
async function getPublicUrls(
  paths: string[],
  client: ReturnType<typeof createClient>,
): Promise<FileItem[]> {
  const output: FileItem[] = []

  for (let idx = 0; idx < paths.length; idx++) {
    const path = paths[idx]
    try {
      // Normalizar path antes de obter URL pública
      const normalizedPath = path
        .trim()
        .replace(/^\/+/, '')
        .replace(/^app\//, '')

      if (!normalizedPath || normalizedPath.length === 0) {
        console.warn(`[getFilesUrls] Path vazio após normalização para: ${path}`)
        continue
      }

      // Tentar obter URL pública
      const { data } = client.storage.from('app').getPublicUrl(normalizedPath)

      if (data?.publicUrl) {
        // Usar a URL pública diretamente
        output.push({ url: data.publicUrl, alt: `item ${idx}` })

        if (process.env.NODE_ENV === 'development') {
          console.log(`[getFilesUrls] URL pública gerada para ${normalizedPath}:`, data.publicUrl)
        }
      } else {
        console.warn(`[getFilesUrls] URL pública não retornada para ${normalizedPath}`)
      }
    } catch (error) {
      console.warn(`[getFilesUrls] Erro ao obter URL pública para ${path}:`, error)
    }
  }

  return output
}
